/**
 * ============================================================
 * DIBAI 18K — Sección Experiencias
 * assets/experiencias-dibai.js  v3.0.0
 *
 * Modos:
 *   grid     → cuadrícula estática
 *   carousel → marquee continuo (conveyor belt via rAF)
 *
 * Lightbox funciona en ambos modos.
 * ============================================================
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────
     HELPERS
  ───────────────────────────────────────── */
  const FOCUSABLE = 'a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])';
  function getFocusable(el) { return Array.from(el.querySelectorAll(FOCUSABLE)); }

  function trapFocus(e, panel) {
    if (e.key !== 'Tab') return;
    const els = getFocusable(panel).filter(el => !el.closest('[hidden]'));
    if (!els.length) return;
    if (e.shiftKey && document.activeElement === els[0]) { e.preventDefault(); els[els.length - 1].focus(); }
    else if (!e.shiftKey && document.activeElement === els[els.length - 1]) { e.preventDefault(); els[0].focus(); }
  }

  function toEmbedUrl(url) {
    if (!url) return null;
    let m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([A-Za-z0-9_-]{11})/);
    if (m) return `https://www.youtube.com/embed/${m[1]}?autoplay=1&rel=0&playsinline=1`;
    m = url.match(/vimeo\.com\/(\d+)/);
    if (m) return `https://player.vimeo.com/video/${m[1]}?autoplay=1`;
    m = url.match(/tiktok\.com\/.+\/video\/(\d+)/);
    if (m) return `https://www.tiktok.com/embed/v2/${m[1]}`;
    return null;
  }

  /* ─────────────────────────────────────────
     LIGHTBOX
  ───────────────────────────────────────── */
  class ExpLightbox {
    constructor(root, slides) {
      this.root    = root;
      this.slides  = slides;
      this.modal   = root.querySelector('[data-exp-lightbox]');
      this.content = root.querySelector('[data-exp-lb-content]');
      this.counter = root.querySelector('[data-exp-lb-counter]');
      this.prevBtn = root.querySelector('[data-exp-lb-prev]');
      this.nextBtn = root.querySelector('[data-exp-lb-next]');
      this.current = 0;
      this._lastFocus = null;
      this._onKey = this._handleKey.bind(this);
      this.modal?.querySelectorAll('[data-exp-lb-close]').forEach(el => el.addEventListener('click', () => this.close()));
      this.prevBtn?.addEventListener('click', () => this.go(this.current - 1));
      this.nextBtn?.addEventListener('click', () => this.go(this.current + 1));
    }
    open(index) {
      if (!this.modal) return;
      this._lastFocus = document.activeElement;
      this.current = index;
      this.modal.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';
      this._render();
      document.addEventListener('keydown', this._onKey);
    }
    close() {
      if (!this.modal) return;
      this._stopMedia();
      this.modal.setAttribute('hidden', '');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this._onKey);
      this._lastFocus?.focus();
    }
    go(index) {
      const total = this.slides.length;
      this.current = ((index % total) + total) % total;
      this._stopMedia(); this._render();
    }
    _render() {
      const slide = this.slides[this.current];
      if (!slide || !this.content) return;
      if (this.counter) this.counter.textContent = `${this.current + 1} / ${this.slides.length}`;
      this.content.innerHTML = '';
      if (slide.type === 'video' && slide.video) {
        const embedUrl = toEmbedUrl(slide.video);
        if (embedUrl) {
          const wrap = document.createElement('div'); wrap.className = 'exp-lb-iframe-wrap';
          const iframe = document.createElement('iframe');
          iframe.src = embedUrl; iframe.allow = 'autoplay; fullscreen; picture-in-picture';
          iframe.allowFullscreen = true; iframe.title = slide.handle || 'Video';
          wrap.appendChild(iframe); this.content.appendChild(wrap);
        } else {
          const video = document.createElement('video'); video.className = 'exp-lb-video';
          video.controls = true; video.autoplay = true; video.playsinline = true;
          const source = document.createElement('source'); source.src = slide.video;
          video.appendChild(source); this.content.appendChild(video);
        }
      } else if (slide.image) {
        const img = document.createElement('img'); img.className = 'exp-lb-image';
        img.src = slide.image; img.alt = slide.handle || 'Experiencia DIBAI 18K'; img.decoding = 'async';
        this.content.appendChild(img);
      }
      if (slide.handle || slide.caption) {
        const cap = document.createElement('div'); cap.className = 'exp-lb-caption';
        if (slide.handle) {
          const h = document.createElement('span'); h.className = 'exp-lb-caption__handle'; h.textContent = slide.handle; cap.appendChild(h);
          if (slide.verified) {
            const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            svg.setAttribute('class', 'exp-lb-caption__verified'); svg.setAttribute('width', '16'); svg.setAttribute('height', '16'); svg.setAttribute('viewBox', '0 0 16 16'); svg.setAttribute('fill', 'none');
            svg.innerHTML = '<circle cx="8" cy="8" r="8" fill="#3B82F6"/><path d="M5 8.5l2 2 4-4" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>';
            cap.appendChild(svg);
          }
        }
        if (slide.caption) { const p = document.createElement('p'); p.className = 'exp-lb-text'; p.textContent = slide.caption; cap.appendChild(p); }
        this.content.appendChild(cap);
      }
      requestAnimationFrame(() => { getFocusable(this.modal)[0]?.focus(); });
    }
    _stopMedia() {
      this.content?.querySelectorAll('video').forEach(v => { v.pause(); v.src = ''; });
      this.content?.querySelectorAll('iframe').forEach(f => { f.src = ''; });
    }
    _handleKey(e) {
      if (e.key === 'Escape')     { this.close(); return; }
      if (e.key === 'ArrowRight') { this.go(this.current + 1); return; }
      if (e.key === 'ArrowLeft')  { this.go(this.current - 1); return; }
      trapFocus(e, this.modal.querySelector('.exp-lightbox__panel'));
    }
  }

  /* ─────────────────────────────────────────
     MARQUEE — scroll continuo sin saltos
     ─────────────────────────────────────────
     Duplica los slides originales en el track.
     rAF mueve el translateX de forma constante.
     Al llegar al ancho del set original, resetea
     el offset a 0 → loop seamless sin ningún corte.
  ───────────────────────────────────────── */
  class ExpMarquee {
    constructor(wrapper, options = {}) {
      this.wrapper     = wrapper;
      this.track       = wrapper.querySelector('[data-exp-track]');
      this.speed       = options.speed || 60;        // px/s
      this.pauseHover  = options.pauseHover !== false;

      if (!this.track) return;

      this._offset   = 0;
      this._paused   = false;
      this._raf      = null;
      this._lastTime = null;
      this._trackW   = 0;

      // Ocultar botones prev/next y dots — no aplican al marquee
      wrapper.querySelectorAll(
        '[data-exp-c-prev],[data-exp-c-next],[data-exp-dots]'
      ).forEach(el => { el.style.display = 'none'; });

      // Quitar transition del track (el rAF maneja el movimiento)
      this.track.style.transition = 'none';

      this._buildLoop();
      this._measure();

      if (this.pauseHover) {
        this.wrapper.addEventListener('mouseenter', () => { this._paused = true; });
        this.wrapper.addEventListener('mouseleave', () => { this._paused = false; });
      }

      this._raf = requestAnimationFrame(this._tick.bind(this));

      this._onResize = () => {
        clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => this._measure(), 150);
      };
      window.addEventListener('resize', this._onResize);
    }

    _buildLoop() {
      // Tomar solo slides originales (no clones previos)
      const origSlides = Array.from(
        this.track.querySelectorAll('.exp-item:not(.exp-marquee-clone)')
      );
      origSlides.forEach(slide => {
        const clone = slide.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.classList.add('exp-marquee-clone');
        // Desactivar lightbox en clones para evitar índices duplicados
        clone.querySelectorAll('[data-exp-open]').forEach(btn => {
          btn.removeAttribute('data-exp-open');
          btn.style.cursor = 'default';
          btn.setAttribute('tabindex', '-1');
        });
        this.track.appendChild(clone);
      });
    }

    _measure() {
      const origSlides = Array.from(
        this.track.querySelectorAll('.exp-item:not(.exp-marquee-clone)')
      );
      const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
      let w = 0;
      origSlides.forEach(s => { w += s.offsetWidth + gap; });
      this._trackW = w;
    }

    _tick(timestamp) {
      if (this._lastTime === null) this._lastTime = timestamp;
      const delta = (timestamp - this._lastTime) / 1000;
      this._lastTime = timestamp;

      if (!this._paused && this._trackW > 0) {
        this._offset += this.speed * delta;
        if (this._offset >= this._trackW) this._offset -= this._trackW;
        this.track.style.transform = `translateX(-${this._offset}px)`;
      }

      this._raf = requestAnimationFrame(this._tick.bind(this));
    }

    destroy() {
      if (this._raf) cancelAnimationFrame(this._raf);
      window.removeEventListener('resize', this._onResize);
    }
  }

  /* ─────────────────────────────────────────
     LAYOUT: mostrar/ocultar grid vs carrusel
  ───────────────────────────────────────── */
  function applyLayout(root) {
    const section  = root.querySelector('.exp-section');
    if (!section) return;
    const layout   = section.dataset.layout || 'grid';
    const grid     = root.querySelector('.exp-grid');
    const carousel = root.querySelector('.exp-carousel-wrapper');
    if (layout === 'carousel') {
      if (grid)     grid.style.display     = 'none';
      if (carousel) carousel.style.display = '';
    } else {
      if (carousel) carousel.style.display = 'none';
      if (grid)     grid.style.display     = '';
    }
  }

  /* ─────────────────────────────────────────
     INICIALIZACIÓN
  ───────────────────────────────────────── */
  function initSection(root) {
    if (!root) return;

    applyLayout(root);

    // JSON de slides
    const dataEl = root.querySelector('[id^="exp-data-"]');
    let slides = [];
    try { slides = JSON.parse(dataEl?.textContent || '[]'); }
    catch (e) { console.warn('[ExpDibai] JSON inválido', e); }

    // Lightbox (solo slides originales del grid o carrusel)
    const lb = new ExpLightbox(root, slides);
    root.querySelectorAll('[data-exp-open]').forEach(btn => {
      btn.addEventListener('click', () => lb.open(parseInt(btn.dataset.expOpen, 10)));
    });

    // Modo carrusel → marquee continuo
    const section = root.querySelector('.exp-section');
    if (section?.dataset.layout === 'carousel') {
      const cw = root.querySelector('[data-exp-carousel-wrapper]');
      if (cw) {
        root._instance = new ExpMarquee(cw, {
          speed:      parseFloat(section.dataset.marqueeSpeed || '60'),
          pauseHover: section.dataset.marqueePause !== 'false'
        });
      }
    }
  }

  function initAll() {
    document.querySelectorAll('.experiencias-dibai-wrapper').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify Theme Editor
  document.addEventListener('shopify:section:load', e => {
    const root = e.target.closest('.experiencias-dibai-wrapper') || e.target.querySelector('.experiencias-dibai-wrapper');
    if (!root) return;
    root._instance?.destroy?.();
    delete root._instance;
    initSection(root);
  });

  document.addEventListener('shopify:section:unload', e => {
    const root = e.target.querySelector?.('.experiencias-dibai-wrapper');
    root?._instance?.destroy?.();
    e.target.querySelector?.('[data-exp-lightbox]:not([hidden])')?.setAttribute('hidden', '');
    document.body.style.overflow = '';
  });

})();
